/**
 * Text, masking and identifier helpers.
 *
 * Masking exists because MicaraHR must never surface an unmasked government
 * identifier or bank account, in demonstration data or otherwise.
 */

/** `Andrea Reyes` → `AR` */
export function initialsOf(fullName: string): string {
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0 && !/^[a-z]{2,3}\.$/i.test(part));
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/** `Andrea Reyes` → `Andrea R.` */
export function shortName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  return `${parts[0]} ${(parts[parts.length - 1] ?? '').charAt(0)}.`;
}

/**
 * Mask all but the last `visible` characters: `1234567890` → `••••••7890`.
 * Never reverse-engineerable, and safe to render anywhere.
 */
export function maskTrailing(value: string, visible = 4, maskChar = '•'): string {
  const clean = value.replace(/\s+/g, '');
  if (clean.length <= visible) return maskChar.repeat(Math.max(clean.length, 4));
  return `${maskChar.repeat(clean.length - visible)}${clean.slice(-visible)}`;
}

/** `09171234567` → `+63 917 ••• 4567` */
export function maskMobileNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 7) return maskTrailing(value);
  const last4 = digits.slice(-4);
  const prefix = digits.length >= 10 ? digits.slice(-10, -7) : digits.slice(0, 3);
  return `+63 ${prefix} ••• ${last4}`;
}

/** `andrea.reyes@micarademo.local` → `a•••••••••@micarademo.local` */
export function maskEmail(value: string): string {
  const [local, domain] = value.split('@');
  if (!local || !domain) return maskTrailing(value);
  return `${local.charAt(0)}${'•'.repeat(Math.max(local.length - 1, 3))}@${domain}`;
}

/** `1234567890` → `••••••7890` (bank / government identifier references). */
export function maskReference(value: string): string {
  return maskTrailing(value, 4);
}

/**
 * `pending_review` → `Pending review`.
 *
 * Sentence case, not title case, because a humanised fallback sits beside
 * catalogue labels like "Needs review" and must not look like a different
 * vocabulary. Screens used to inline `status.replace(/_/g, ' ')`, which left
 * the label entirely lowercase.
 */
export function humanizeCode(code: string): string {
  const words = code.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (words.length === 0) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

/** `3 pending requests` / `1 pending request` */
export function countLabel(count: number, singular: string, plural?: string): string {
  return `${count} ${pluralize(count, singular, plural)}`;
}

/**
 * Deterministic, dependency-free id generator.
 *
 * The mock services must produce stable, readable ids without pulling in a
 * crypto polyfill. `prefix_<base36 counter>_<seeded suffix>`.
 */
export function createIdFactory(prefix: string, seed = 1): () => string {
  let counter = seed;
  return () => {
    counter += 1;
    const stamp = counter.toString(36).padStart(4, '0');
    return `${prefix}_${stamp}`;
  };
}

/** Stable reference codes shown to employees, e.g. `LV-2026-0042`. */
export function referenceCode(prefix: string, year: number, sequence: number): string {
  return `${prefix}-${year}-${String(sequence).padStart(4, '0')}`;
}

/** Simple, stable hash used for dedupe keys and deterministic seeding. */
export function stableHash(value: string): number {
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return Math.abs(hash);
}
